import { log, logMethodCall } from '../utils/debug';
import {
  BANNER_BEHAVIOUR,
  WIDGET,
} from '../const/common';
import {
  isInViewport,
} from '../utils/dom';
import { markElementAsShown } from '../utils/scriptFunctions';
import Event from '../const/event';
import '../cleverpush-widget';

export function initWidgets(context) {
  logMethodCall('initWidgets');

  const storageKey = 'cleverpush-widgets-shown';
  const shownWidgetsStorage = localStorage.getItem(storageKey);

  let localWidgetIds;
  let shownWidgets;
  if (shownWidgetsStorage) {
    try {
      localWidgetIds = JSON.parse(shownWidgetsStorage);
      // Ensure localWidgetIds is an array before spreading
      if (Array.isArray(localWidgetIds)) {
        shownWidgets = [...localWidgetIds];
      } else {
        localWidgetIds = [];
        shownWidgets = [];
      }
    } catch (error) {
      log.info('error parsing JSON', error);
      localWidgetIds = [];
    }
  }
  if (!shownWidgets) {
    shownWidgets = [];
  }
  if (!localWidgetIds) {
    localWidgetIds = [];
  }
  const shownWidgetsSessionStorage = sessionStorage.getItem(storageKey);
  let sessionWidgetIds;
  if (shownWidgetsSessionStorage) {
    try {
      sessionWidgetIds = JSON.parse(shownWidgetsSessionStorage);
      shownWidgets = shownWidgets.concat(sessionWidgetIds);
    } catch (error) {
      log.info('error parsing JSON', error);
    }
  }

  const updateStorage = (storage, key, data, bannerId) => {
    try {
      storage.setItem(key, JSON.stringify(data));
      shownWidgets = shownWidgets.filter((id) => id !== bannerId);
    } catch (ignored) {
      // ignore
    }
  };

  const handleCleanStorage = (widget) => {
    if (!widget) {
      return;
    }
    const isWidgetInSession = (sessionWidgetIds || []).includes(widget._id);
    const isBannerInLocal = (localWidgetIds || []).includes(widget._id);

    if (isWidgetInSession && ![BANNER_BEHAVIOUR.ONCE_PER_SESSION, BANNER_BEHAVIOUR.EVERY_X_SESSIONS].includes(widget.frequency)) {
      sessionWidgetIds = sessionWidgetIds.filter((id) => id !== widget._id);
      updateStorage(sessionStorage, storageKey, sessionWidgetIds, widget._id);
      const sessionKey = `${storageKey}-sessions-${widget._id}`;
      if (localStorage.getItem(sessionKey)) {
        localStorage.removeItem(sessionKey);
      }
      if (sessionStorage.getItem(sessionKey)) {
        sessionStorage.removeItem(sessionKey);
      }
    }
    if (isBannerInLocal && widget.frequency !== BANNER_BEHAVIOUR.ONCE) {
      localWidgetIds = localWidgetIds.filter((id) => id !== widget._id);
      updateStorage(localStorage, storageKey, localWidgetIds);
    }
    if ((widget.frequency === BANNER_BEHAVIOUR.ALWAYS || context.config.env === 'PREVIEW') && shownWidgets.includes(widget._id)) {
      shownWidgets = shownWidgets.filter((id) => id !== widget._id);
    }
  };

  const isWidgetShown = (widget) => {
    if (!widget) {
      return false;
    }
    handleCleanStorage(widget);
    if (widget?.frequency === BANNER_BEHAVIOUR.EVERY_X_SESSIONS) {
      const sessionKey = `${storageKey}-sessions-${widget._id}`;
      const sessions = parseInt(localStorage.getItem(sessionKey), 10) || 0;
      if (sessions < widget.frequencyInterval && sessions > 0) {
        if (!sessionStorage.getItem(sessionKey)) {
          sessionStorage.setItem(sessionKey, `${sessions + 1}`);
          localStorage.setItem(sessionKey, `${sessions + 1}`);
        }
        if (!shownWidgets.includes(widget._id)) {
          shownWidgets = [...shownWidgets, widget._id];
        }
      }
    }
    return shownWidgets.includes(widget._id);
  };

  context.widgets = (context.widgets || []).filter((widget) => widget && !shownWidgets.includes(widget._id));
  (context.config.widgets || []).filter(Boolean).filter((widget) => widget.autoAppendType === 'selector').forEach((widget) => {
    try {
      if (widget.autoAppendPath) {
        if (!new RegExp(widget.autoAppendPath).test(window.location.pathname)) {
          return;
        }
      }
      if (widget.autoAppendSelector) {
        const target = document.querySelector(widget.autoAppendSelector);
        if (target) {
          const widgetDiv = document.createElement('div');
          widgetDiv.className = 'cleverpush-widget';
          widgetDiv.dataset.id = widget._id;
          if (!widget.autoAppendPosition || widget.autoAppendPosition === 'top') {
            target.insertBefore(widgetDiv, target.firstChild);
          } else if (widget.autoAppendPosition === 'bottom') {
            target.appendChild(widgetDiv);
          }
        }
      }
    } catch (e) {
      log.warn('Failed appending widget', e);
    }
  });

  let hasExitIntent = false;
  const shownWidget = false;
  const widgets = document.querySelectorAll('.cleverpush-content-button, .cleverpush-widget');
  if (!widgets.length) {
    return;
  }

  import('./index').then(async ({ default: Widget }) => {
    Promise.all([...widgets].map(async (element, i) => {
      const widgetObject = (context.config.widgets || []).find((widget) => widget && widget._id === element.dataset.id);
      if (widgetObject && isWidgetShown(widgetObject)) {
        return;
      }
      const isWidgetAlreadyExist = (context.config.widgets || []).some((widget) => widget && widget._id === element.dataset.id);

      // Only require a data-id and fetch remote config for real widgets (not content buttons)
      if (element.classList.contains('cleverpush-widget') && !isWidgetAlreadyExist) {
        if (!element.dataset.id) {
          return;
        }

        const newWidget = await context.api.getWidget(element.dataset.id);
        context.config.widgets.push(newWidget);
      }

      if (element.dataset.widgetType === 'whatsapp-widget') {
        return;
      }

      const widget = new Widget(i, element, context.config, context.subscriptionManager, context.api, context.trigger.bind(context));
      const widgetConfig = widget.elementConfig;

      const bypassExitIntent = context.isMobile && widgetConfig.enableRegularDisplayForMobile && !widgetConfig.exitIntentForMobile;

      const isMessengerWidget = widget.isMessengerWidget('telegramChannel') || widget.isMessengerWidget('facebookChannel');
      if (context.webPushUnsupported && !isMessengerWidget && !widget.hasSharedWidgetChannels()) {
        // do not show widget when web push is not supported and this is not a messenger widget
        return;
      }

      if (widgetConfig.exitIntent && !bypassExitIntent) {
        if (hasExitIntent) {
          return;
        }

        if (context.isMobile && widgetConfig.exitIntentForMobile && !widgetConfig.enableRegularDisplayForMobile) {
          import('../utils/scriptFunctions').then(({ initMobileExitIntentListeners }) => {
            initMobileExitIntentListeners(WIDGET, shownWidget, element, widget, shownWidgets, storageKey, context);
          });
        }

        hasExitIntent = true;

        context.initElementExitIntentListener(WIDGET, shownWidget, element, widget, shownWidgets, storageKey);
      } else {
        import('../webBanner/initWebBanners').then(({ shouldShowBanner }) => {
          shouldShowBanner(false, widget, context).then((show) => {
            if (!show) {
              return;
            }

            setTimeout(() => {
              context.on(Event.SUBSCRIBED, () => {
                // use show instead of reset to re-paint topics
                widget.show(true);
              });
              context.on(Event.TOPICS_SET, () => {
                widget.show(true);
              });
              context.on(Event.TAGS_SET, () => {
                widget.show(true);
              });
              context.on(Event.UNSUBSCRIBED, () => {
                widget.show(false);
              });

              if (!context.subscriptionManager) {
                widget.show(false);
              }

              context.subscriptionManager?.isSubscribed().then((isSubscribed) => {
                widget.show(isSubscribed);

                // track confirm alert only when widget is visible
                if (widget.elementConfig.id && widget.element) {
                  if (isInViewport(widget.element)) {
                    widget.trackShown();
                  } else {
                    const enterViewportListener = () => {
                      if (isInViewport(widget.element)) {
                        window.removeEventListener('scroll', enterViewportListener);
                        widget.trackShown();
                      }
                    };
                    window.addEventListener('scroll', enterViewportListener, false);
                  }
                }
              });
              markElementAsShown(widgetObject, storageKey);
              context.widgets.push(widget);
            }, (widget.timeout || 0) * 1000);
          });
        });
      }
    })).catch((err) => {
      log.error(err);
    });
  });
}
